import Papa from 'papaparse';

export type ExtractedSource =
  | { kind: 'text'; text: string }
  | { kind: 'csv'; rows: string[][] };

/** Try to read a File as text. */
export async function readAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = () => reject(fr.error);
    fr.onload = () => resolve(String(fr.result || ''));
    fr.readAsText(file);
  });
}

/** Read a File as ArrayBuffer. */
export async function readAsArrayBuffer(file: File): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onerror = () => reject(fr.error);
    fr.onload = () => resolve(fr.result as ArrayBuffer);
    fr.readAsArrayBuffer(file);
  });
}

/** Parse a CSV/text file into a 2D string matrix. Auto-detects delimiter. */
export async function parseCsv(file: File): Promise<string[][]> {
  const text = await readAsText(file);
  const result = Papa.parse<string[]>(text.trim(), {
    delimiter: '',           // auto-detect
    skipEmptyLines: true,
  });
  return (result.data as string[][]).filter(r => r.length && r.some(c => c && c.trim()));
}

/**
 * Extract plain text from a PDF using unpdf (lazy import keeps the
 * parser chunk out of the main bundle).
 */
export async function extractPdfText(file: File): Promise<string> {
  const { extractText, getDocumentProxy } = await import('unpdf');
  const buffer = await readAsArrayBuffer(file);
  // unpdf accepts a Uint8Array
  const u8 = new Uint8Array(buffer);
  const pdf = await getDocumentProxy(u8);
  const { text } = await extractText(pdf, { mergePages: true });
  // text is string | string[] depending on version
  return Array.isArray(text) ? text.join('\n') : text;
}

export async function extractFromFile(file: File): Promise<ExtractedSource> {
  const name = file.name.toLowerCase();
  if (name.endsWith('.csv') || name.endsWith('.txt')) {
    const rows = await parseCsv(file);
    return { kind: 'csv', rows };
  }
  if (name.endsWith('.pdf')) {
    const text = await extractPdfText(file);
    return { kind: 'text', text };
  }
  // Default: try as CSV, fall back to text
  try {
    const rows = await parseCsv(file);
    if (rows.length) return { kind: 'csv', rows };
  } catch { /* ignore */ }
  const text = await readAsText(file);
  return { kind: 'text', text };
}
